<script lang="ts">
    import { formatNumberShort } from "@/utils/formatNumberShort/formatNumberShort";
    import WishlistTheme from "@apps/wishlist/components/WishlistTheme/WishlistTheme.svelte";
    import { SAVE_WISHLIST_MODAL_ACTION_TYPES } from "@apps/wishlist/constants/constants";
    import { SaveWishlistModalWidgetDefinition } from "@apps/wishlist/definitions";
    import { WishlistProductStatusesQuery } from "@apps/wishlist/queries/WishlistProductStatusesQuery";
    import { WishlistProductStatusesService } from "@apps/wishlist/services/WishlistProductStatusesService";
    import { WishlistsService } from "@apps/wishlist/services/WishlistsService";
    import WishlistsStorefrontValuesService from "@apps/wishlist/services/WishlistsStorefrontValuesService";
    import { saveWishlistModalToken } from "@apps/wishlist/tokens";
    import { ProductIdManagerInterface } from "@lib/ProductIdManager/ProductIdManagerInterface";
    import { ProductVariantIdManagerInterface } from "@lib/ProductVariantIdManager/ProductVariantIdManagerInterface";
    import { AuthenticationInfoProviderInterface } from "@modules/authentication/AuthenticationInfoProvider/AuthenticationInfoProviderInterface";
    import { ROLE } from "@modules/authentication/constants";
    import { DynamicImportManager } from "@modules/dynamic_import/DynamicImportManager";
    import { I18nModule } from "@modules/i18n/I18nModule";
    import ShopifyAnalyticsManager from "@modules/shopify_analytics/ShopifyAnalyticsManager";
    import CN from "classnames";
    import { lastValueFrom, map, of, switchMap } from "rxjs";
    import { onMount } from "svelte";
    import { container } from "tsyringe";

    import { ModalManager } from "@components/Modal/ModalManager";
    import WishlistIcon from "@components/WishlistIcon/WishlistIcon.svelte";

    export let wishlistProductStatusesService: WishlistProductStatusesService;
    export let wishlistProductStatusesQuery: WishlistProductStatusesQuery;
    export let modalManager: ModalManager;
    export let authenticationInfoProvider: AuthenticationInfoProviderInterface;
    export let wishlistsStorefrontValuesService: WishlistsStorefrontValuesService;
    export let wishlistsService: WishlistsService;
    export let dynamicImportManager: DynamicImportManager;
    export let productIdManager: ProductIdManagerInterface;
    export let productVariantIdManager: ProductVariantIdManagerInterface;
    export let shopifyAnalyticsManager: ShopifyAnalyticsManager | null;
    export let afterWidgetInject: () => void;
    export let wishlistCounterPosition: "left-top" | "right-top";

    const wishlistBranding$ =
        wishlistsStorefrontValuesService.selectWishlistsBranding();

    const settings$ =
        wishlistsStorefrontValuesService.selectWishlistsSettings();

    const productId$ = productIdManager.productId$;
    const variantId$ = productVariantIdManager.variantId$;

    const productStatus$ = productId$.pipe(
        switchMap((productId) => {
            return productId
                ? wishlistProductStatusesQuery.selectEntity(productId)
                : of();
        })
    );

    const productInWishlistStatus$ = productId$.pipe(
        switchMap((productId) => {
            return productId
                ? wishlistProductStatusesQuery.selectProductInWishlistStatus(
                      productId
                  )
                : of();
        })
    );

    const loading$ = productId$.pipe(
        switchMap((productId) => {
            return productId
                ? wishlistProductStatusesQuery.selectProductInLoadingStatus(
                      productId
                  )
                : of();
        })
    );

    const isCustomer$ = authenticationInfoProvider
        .selectRole()
        .pipe(map((role) => role === ROLE.Customer));

    onMount(() => {
        const productIdSubscription = productId$.subscribe((productId) => {
            if (productId) {
                wishlistProductStatusesService.addProductIdForFetching(
                    productId
                );
            }
        });

        afterWidgetInject();

        return () => {
            productIdSubscription.unsubscribe();
        };
    });

    let handleToggle = () => {
        if ($productInWishlistStatus$ && $productId$) {
            lastValueFrom(
                wishlistsService.deleteItem({
                    productId: $productId$,
                    variantId: null,
                    wishlistId: null,
                })
            );
        } else {
            handleAddItem();
        }
    };

    const handleOpenSaveModal = async () => {
        await dynamicImportManager.fetch({
            definition: SaveWishlistModalWidgetDefinition,
        });
        // Ensure the modal widget initializes asynchronously before opening it.
        setTimeout(() => {
            modalManager.openModal(saveWishlistModalToken, undefined, {
                actionType: SAVE_WISHLIST_MODAL_ACTION_TYPES.addItem,
                productId: $productId$,
                variantId: $variantId$,
            });
        }, 0);
    };

    const handleAddItem = () => {
        if (!$settings$) return;

        if ($settings$.isDisabledForGuests && !$isCustomer$) {
            const email = wishlistsService.getGuestEmailFromLocalStorage();

            if (email) {
                if (!$productId$) return;
                lastValueFrom(
                    wishlistsService.saveGuestItem({
                        email,
                        productId: $productId$,
                        variantId: $variantId$ || null,
                    })
                );

                if (shopifyAnalyticsManager) {
                    shopifyAnalyticsManager.sendVisitorInfo({ email });
                }
            } else {
                handleOpenSaveModal();
            }
        } else if ($productId$) {
            lastValueFrom(
                wishlistsService.addItem({
                    productId: $productId$,
                    variantId: $variantId$,
                    wishlistId: null,
                })
            );
        }
    };

    const i18nModule = container.resolve(I18nModule);
    const add_to_wishlist_button_accessibility_label$ =
        i18nModule.exports.i18n.selectT(
            "wishlist.critical.AddToWishlistProductCardWidget",
            "add_to_wishlist_button_accessibility_label"
        );
    const remove_from_wishlist_button_accessibility_label$ =
        i18nModule.exports.i18n.selectT(
            "wishlist.critical.AddToWishlistProductCardWidget",
            "remove_from_wishlist_button_accessibility_label"
        );

    const button_accessibility_label$ = productInWishlistStatus$.pipe(
        switchMap((productInWishlistStatus) =>
            productInWishlistStatus
                ? remove_from_wishlist_button_accessibility_label$
                : add_to_wishlist_button_accessibility_label$
        )
    );
</script>

{#if $wishlistBranding$}
    <WishlistTheme branding={$wishlistBranding$}>
        <button
            class={CN("gw-toggle-wishlist-product-card-button", {
                "gw-toggle-wishlist-product-card-button--loading": $loading$,
            })}
            data-in-wishlist={!!$productInWishlistStatus$}
            on:click={handleToggle}
            aria-label={$button_accessibility_label$}
        >
            <WishlistIcon
                width={20}
                height={20}
                isFilled={$productInWishlistStatus$}
                iconSettings={$wishlistBranding$.wishlistIconSettings}
            />

            {#if $wishlistBranding$.wishlistButtonSettings.isCounterEnabled && $productStatus$ && $productStatus$.wishlistItemsCount !== 0}
                <div
                    class="gw-toggle-wishlist-product-card-button__counter"
                    class:gw-toggle-wishlist-product-card-button__counter--left-top={wishlistCounterPosition ===
                        "left-top"}
                    class:gw-toggle-wishlist-product-card-button__counter--right-top={wishlistCounterPosition ===
                        "right-top"}
                >
                    {formatNumberShort($productStatus$.wishlistItemsCount)}
                </div>
            {/if}
        </button>
    </WishlistTheme>
{/if}

<style lang="postcss">
    .gw-toggle-wishlist-product-card-button {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 0;
        width: 32px;
        height: 32px;
        padding: 0;
        border-radius: 50%;
        margin: 0;
        background: none;
        cursor: pointer;
        background: var(--gw-wl-wishlistIconSettings-backgroundColor);
        color: var(--gw-wl-wishlistIconSettings-heartColor);
        box-shadow: 0px 8px 16px -8px #3737371f;
    }

    .gw-toggle-wishlist-product-card-button__counter {
        position: absolute;
        background-color: var(--gw-wl-wishlistIconSettings-heartColor);
        color: var(--gw-wl-wishlistIconSettings-backgroundColor);
        font-size: 10px;
        padding: 1px 3px;
        text-align: center;
        min-width: 15px;
        box-sizing: border-box;
        border: 1px solid var(--gw-wl-wishlistIconSettings-backgroundColor);
        border-radius: 30px;

        &--left-top {
            top: 0;
            right: 50%;
            transform: translate(-10%, -20%);
        }

        &--right-top {
            top: 0;
            left: 50%;
            transform: translate(10%, -20%);
        }
    }

    .gw-toggle-wishlist-product-card-button__counter-skeleton {
        margin-left: 6px;
        width: 1.5em;
        height: 0.9em;
        background-color: var(--gw-theme__main-color-lighten-10);
        border-radius: 3px;
    }
</style>
